/** API client for script rebuild workflow. */

import axios, { type AxiosResponse } from 'axios';
import { auth } from '@/lib/firebase';
import type {
  RebuildDocumentResponse,
  RebuildSession,
  RebuildSessionResponse,
  SelectedRecommendation,
} from '@/types/rebuild';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const rebuildApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

rebuildApi.interceptors.request.use(async (config) => {
  if (auth.currentUser) {
    try {
      const token = await auth.currentUser.getIdToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
    } catch (e) {
      console.error('Error getting Firebase token:', e);
    }
  }
  return config;
});

export async function getRebuildSession(
  analysisSessionId: string,
): Promise<RebuildSessionResponse> {
  const res: AxiosResponse<{ status: string; session: RebuildSession; available_recommendations: RebuildSessionResponse['available_recommendations'] }> =
    await rebuildApi.get(`/api/rebuild/${analysisSessionId}`);
  return {
    session: res.data.session,
    available_recommendations: res.data.available_recommendations,
  };
}

export async function startRebuild(
  analysisSessionId: string,
  selected: SelectedRecommendation[],
): Promise<{ rebuild_id: string }> {
  const res = await rebuildApi.post(`/api/rebuild/${analysisSessionId}/start`, {
    selected_recommendations: selected,
  });
  return { rebuild_id: res.data.rebuild_id ?? analysisSessionId };
}

export async function getRebuildStatus(rebuildId: string): Promise<{
  status: string;
  progress?: RebuildSession['progress'];
  error?: string | null;
}> {
  const res = await rebuildApi.get(`/api/rebuild/${rebuildId}/status`);
  return res.data;
}

export async function getRebuildDocument(rebuildId: string): Promise<RebuildDocumentResponse> {
  const res: AxiosResponse<{ status: string } & RebuildDocumentResponse> = await rebuildApi.get(
    `/api/rebuild/${rebuildId}/document`,
  );
  return {
    original_text: res.data.original_text,
    current_text: res.data.current_text,
    edits: res.data.edits,
    session: res.data.session,
  };
}

export async function patchRebuildEdit(
  rebuildId: string,
  editId: string,
  status: 'applied' | 'reverted',
): Promise<RebuildSession> {
  const res = await rebuildApi.patch(`/api/rebuild/${rebuildId}/edits/${editId}`, { status });
  return res.data.session;
}

export async function saveRebuildContent(
  rebuildId: string,
  currentText: string,
): Promise<RebuildSession> {
  const res = await rebuildApi.put(`/api/rebuild/${rebuildId}/content`, {
    current_text: currentText,
  });
  return res.data.session;
}

export async function exportRebuild(rebuildId: string): Promise<RebuildSession> {
  const res = await rebuildApi.post(`/api/rebuild/${rebuildId}/export`);
  return res.data.session;
}
